/**
 * Shape the config language emits for an action whose component called back
 * with a bare scalar.
 *
 * `processActions` wraps such an argument as `{ value }` — spreading a string
 * would otherwise yield a char-indexed object, so `{{this}}` would resolve to
 * `{0:'C',1:'A',...}`. A storefront therefore authors `{{this.value}}`.
 */
export interface ScalarActionArgs {
  value?: unknown
}

/**
 * The first candidate that is a non-empty string, or `''`.
 *
 * Every field on an action's args is `unknown`, because the values arrive from
 * remote JSON through `processData` and a storefront can author anything.
 * Handlers pass the named field first and `value` second, so one handler
 * accepts both authoring styles:
 *
 * ```
 * args: { methodId: '{{this.value}}' }   // the component called back with a
 *                                        // bare id
 * args: { methodId: '{{this.methodId}}' } // it called back with an object
 * ```
 *
 * Returns `''` rather than `undefined` so a caller can guard with a single
 * falsy test and never has to tell an unauthored arg from an empty one — both
 * mean "no usable value", and both leave the shopper where they are.
 *
 * Lifted out of `hooks/orders/useOrderActions.ts`, where it was file-local and
 * the only copy, when the checkout pickers needed the same coercion.
 */
export const readString = (...candidates: unknown[]): string => {
  const found = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.length > 0,
  )
  return typeof found === 'string' ? found : ''
}

export default readString
